import { putSynchedBooking } from "@/api/booking/booking";
import DatabaseService from "@/services/database.service";
import { LogLevel, LogService } from "@/services/logging";
import { BookingState, IUpdatedBooking } from "@/types/db/product-slot-booking";
import { cloneDeep } from "lodash";
import { WithDeleted } from "rxdb/dist/types/types";
import BookingModule from "@/store/modules/booking";
import { checkAndSynchUpdatedBookingFiles } from "@/utils/vue-helpers/pos-files";
import UserModule from "@/store/modules/user";
import { SyncType } from "@/interfaces/store/IBooking";

export async function saveUpdatedBooking(diffObjects: any) {
  // Lets first check if the updatedBooking is empty
  // If it is empty we don't insert it
  // because it will create synching issues for us
  const isEmpty = isEmptyBooking(diffObjects);
  if (isEmpty) {
    LogService.info(
      `Error: Empty updatedBooking captured, uuid:${diffObjects.booking.uuid}`,
      { params: diffObjects },
      LogLevel.Error
    );
    return;
  }
  LogService.info(`Storing updated bookings into local database`, diffObjects);
  const db = await DatabaseService.get();
  const updatedBookings = db.updatedbookings;
  try {
    const diffObjectsCopy = cloneDeep(diffObjects);
    await updatedBookings.insert(diffObjectsCopy);
  } catch (err: any) {
    LogService.info(
      `Error when storing updated booking into local db`,
      { params: { diffObjects: diffObjects, err: err } },
      LogLevel.Error
    );
    throw err;
  }
  return true;
}

function isEmptyBooking(diffObject: any): boolean {
  const updatedBooking = diffObject.diffObject;
  if (updatedBooking.booking.state === BookingState.PendingInfo) {
    if (
      !updatedBooking.accountingUpdates.length &&
      !updatedBooking.accountingEntriesUpdates.length &&
      !updatedBooking.newAccountings.length &&
      !updatedBooking.newAccountingEntries.length &&
      !updatedBooking.newPSS.length &&
      !updatedBooking.pssUpdates.length &&
      !updatedBooking.newBookingCustomFieldsAnswers.length &&
      !updatedBooking.updatedBookingCustomFieldsAnswers.length &&
      !updatedBooking.newPSSCustomFieldsAnswers.length &&
      !updatedBooking.updatedPSSCustomFieldsAnswers.length
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Push sync call for pushing data from local db (updatedbookings) to Smeetz backend.
 * @param diffObject differences between original booking pulled from backend and the booking in its final shape after editing it
 * LOGIC:
 * We start processing the array of updatedBookings from the end, why?
 * Because if the first updatedBooking has already tried synching but backend didn't accept it
 * then we will start synching with the newest updatedBooking so that we avoid getting in a loop of
 * error response whenever trying to synch the first updatedBooking.
 * This means if first updatedBooking has an issue then synching will work for the rest of the updatedBookings
 * This will be useless only in the case of having 3 updatedBookings in a row and all with issues
 */
export async function updatedBookingsPushHandler(
  diffObject: WithDeleted<IUpdatedBooking>[]
) {
  for (let i = diffObject.length; i >= 0; i--) {
    // Clone objects so that we don't manipulate original data
    const objCopy = cloneDeep(diffObject[i]);
    if (objCopy) {
      LogService.info(
        `Doing PUT synchronize-bookings of edited booking with id: ${objCopy._id}`,
        { params: objCopy }
      );
      try {
        const res = await putSynchedBooking(objCopy.diffObject);
        //if backend responds that booking is successfully synced, we remove it from the list of pendingSyncBookings

        if (res.status === "success") {
          BookingModule.removeSyncedBookingFromPendingSyncBookings(
            //need to put uuid in the array, because function removeSyncedBookingFromPendingSyncBookings compares two arrays, not strings
            {
              synchedBooking: [res.data.updatedBooking],
              typeToDelete: SyncType.PUT,
            }
          );

          // If bookings synched has custom fields files in them, then we need to insert the files IDs into their DB
          // to trigger their synching process
          LogService.info(
            `Checking if there are files to synch for updated-booking: ${objCopy.uuid}`
          );
          await checkAndSynchUpdatedBookingFiles(objCopy);
        }
      } catch (error: any) {
        // If error is coming from backend logic (i.e: data we posted are correct) then log it and don't try again
        // otherwise, throw the error so that it tries synching again
        const data = error && error.response && error.response.data;
        const msg =
          data.messages && data.messages.length ? data.messages[0] : "";
        if (
          msg.includes("SMEETZPOS") &&
          // do not ignore put requests that failed because booking doesn't exist already on backend
          !msg.includes("not found")
        ) {
          // log the error and don't try again
          LogService.info(
            `SMEETZPOS Error when PUT booking with id: ${objCopy._id}`,
            { params: { errorMessage: msg } },
            LogLevel.Error
          );
        } else {
          if (UserModule.isColleague) {
            // This is a colleague, we log what shouldn't be alerted in Slack
            LogService.info(
              `Error (From Colleague) when PUT booking with id: ${objCopy._id}`,
              { params: { errorMessage: msg } },
              LogLevel.Error
            );
          } else {
            // This is not a colleague, we log what should be alerted in Slack
            LogService.info(
              `Error when PUT booking with id: ${objCopy._id}`,
              { params: { errorMessage: msg } },
              LogLevel.Error
            );
          }
          // Below is commented because we're manually throwing errors only if one of the bookings hasn't been synched successfully
          // we will try again
          throw error;
          // return error;
        }
      }
    }
  }
}
